import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ADMIN_FORCE_UPDATE_ALL_CPM_CAMPAIGN_SUBMISSION_VIEWS } from '@/queries/queries';
import type { CampaignRanking } from '@/queries/cpmRanking';

export function RankingMetricsRefresh({ ranking, example = false }: { ranking: CampaignRanking; example?: boolean }) {
  const { t } = useTranslation();
  const [request] = useMutation<{ adminForceUpdateAllCpmCampaignSubmissionViews: number }>(ADMIN_FORCE_UPDATE_ALL_CPM_CAMPAIGN_SUBMISSION_VIEWS);
  const [feedback, setFeedback] = useState<{ kind: 'queued'; count: number } | { kind: 'nothingToUpdate' | 'failed' } | null>(null);
  const [pending, setPending] = useState(false);
  const generation = useRef(0);
  const activeRequest = useRef(false);
  const identity = `${ranking.campaign_id}:${ranking.phase}:${ranking.can_review}:${example}`;
  useEffect(() => {
    generation.current += 1;
    activeRequest.current = false;
    setPending(false);
    setFeedback(null);
    return () => { generation.current += 1; };
  }, [identity]);
  const disabled = pending || !ranking.can_review || example || ranking.phase === 'finalized';
  const refresh = async () => {
    if (disabled || activeRequest.current) return;
    const current = generation.current;
    activeRequest.current = true;
    setPending(true);
    setFeedback(null);
    try {
      const result = await request({ variables: { campaignId: ranking.campaign_id } });
      const count = result.data?.adminForceUpdateAllCpmCampaignSubmissionViews;
      if (typeof count !== 'number' || !Number.isSafeInteger(count) || count < 0) throw new Error('Invalid enqueue count');
      if (current === generation.current) setFeedback(count > 0 ? { kind: 'queued', count } : { kind: 'nothingToUpdate' });
    } catch {
      if (current === generation.current) setFeedback({ kind: 'failed' });
    } finally {
      if (current === generation.current) { activeRequest.current = false; setPending(false); }
    }
  };
  return <section className="space-y-2 rounded-lg border bg-muted/20 p-4">
    <RankingMetricsStatus ranking={ranking} />
    {ranking.can_review && <Button type="button" size="sm" variant="outline" disabled={disabled} onClick={() => void refresh()}><RefreshCw aria-hidden="true" className={`mr-2 h-4 w-4 ${pending ? 'animate-spin' : ''}`} />{t('ranking.metrics.request')}</Button>}
    {feedback && <p role={feedback.kind === 'failed' ? 'alert' : 'status'} className={`text-sm ${feedback.kind === 'failed' ? 'text-destructive' : 'text-muted-foreground'}`}>{t(`ranking.metrics.${feedback.kind}`, feedback.kind === 'queued' ? { count: feedback.count } : undefined)}</p>}
  </section>;
}

/** Participant-visible freshness only; requesting provider work stays in the audit. */
export function RankingMetricsStatus({ ranking }: { ranking: Pick<CampaignRanking, 'last_view_sync_at' | 'config'> }) {
  const { t, i18n } = useTranslation();
  const timestamp = ranking.last_view_sync_at ? Date.parse(ranking.last_view_sync_at) : NaN;
  return <div className="space-y-2">
    <p className="text-sm">{Number.isFinite(timestamp) ? t('ranking.metrics.lastUpdated', { time: new Date(timestamp).toLocaleString(i18n?.resolvedLanguage || i18n?.language, { timeZone: ranking.config.timezone }) }) : t('ranking.metrics.neverUpdated')}</p>
    <p className="text-xs text-muted-foreground">{t('ranking.metrics.help')}</p>
  </div>;
}
